import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { supabase } from '@/lib/supabase';
import { toast } from 'sonner';
import { 
  ArrowLeft,
  Save,
  Copy,
  ChevronLeft,
  ChevronRight,
  FileText,
  Settings,
  Image,
  CheckCircle,
  Circle,
  BookOpen,
  Upload,
  Plus,
  TrendingUp,
  Activity
} from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";

import { Course, CourseFamily, CourseMode, CourseModes } from '@/types';
import {
  generateUniqueCode,
  generateCourseNumber as generateCourseNumberValue,
  generateFallbackDisplayId
} from '@/lib/dbUtils';
import ImageUpload from '@/components/ui/image-upload';
import { uploadCourseImage } from '@/services/uploadService';
import { cn } from '@/lib/utils';

const CourseForm = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const isEditMode = !!id;
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [courseFamilies, setCourseFamilies] = useState<CourseFamily[]>([]);
  
  // Form states
  const [formName, setFormName] = useState('');
  const [formDescription, setFormDescription] = useState('');
  const [formRequirements, setFormRequirements] = useState('');
  const [formFamilyId, setFormFamilyId] = useState<string | undefined>(undefined);
  const [formModes, setFormModes] = useState<CourseModes>(['onlineAsync']);
  const [formDurationOnlineAsync, setFormDurationOnlineAsync] = useState<number | undefined>(undefined);
  const [formDurationOnlineSync, setFormDurationOnlineSync] = useState<number | undefined>(undefined);
  const [formDurationInPerson, setFormDurationInPerson] = useState<number | undefined>(undefined);
  const [formDurationMinutes, setFormDurationMinutes] = useState<number | undefined>(undefined);
  const [formLearningGoals, setFormLearningGoals] = useState('');
  const [formCoverUrl, setFormCoverUrl] = useState('');
  const [formThumbnailUrl, setFormThumbnailUrl] = useState('');
  const [formLessonPlanUrl, setFormLessonPlanUrl] = useState('');
  const [formLessonSlidesUrl, setFormLessonSlidesUrl] = useState('');
  const [formCourseNumber, setFormCourseNumber] = useState('');
  
  // Upload states
  const [pendingCoverFile, setPendingCoverFile] = useState<File | null>(null);
  const [pendingThumbnailFile, setPendingThumbnailFile] = useState<File | null>(null);
  const [currentStep, setCurrentStep] = useState(1);
  const [completedSteps, setCompletedSteps] = useState<number[]>([]);
  
  const steps = [
    { 
      id: 1, 
      title: 'Informações Básicas', 
      description: 'Nome, família e modalidades',
      icon: FileText 
    },
    { 
      id: 2, 
      title: 'Conteúdo', 
      description: 'Descrição e objetivos',
      icon: Settings 
    },
    { 
      id: 3, 
      title: 'Recursos', 
      description: 'Imagens e materiais',
      icon: Image 
    }
  ];

  useEffect(() => {
    fetchCourseFamilies();
    if (isEditMode) {
      fetchCourse();
    }
  }, [id]);

  const fetchCourseFamilies = async () => {
    try {
      const { data, error } = await supabase
        .from('course_families')
        .select('*')
        .order('name');

      if (error) throw error;
      
      setCourseFamilies(data);
    } catch (error) {
      console.error('Erro ao buscar famílias de cursos:', error);
      toast.error('Não foi possível carregar as famílias de cursos');
    }
  };

  const fetchCourse = async () => {
    if (!id) return;
    
    setLoading(true);
    try {
      const { data, error } = await supabase
        .from('courses')
        .select('*')
        .eq('id', id)
        .single();

      if (error) throw error;
      
      setFormName(data.name || '');
      setFormDescription(data.description || '');
      setFormRequirements(data.requirements || '');
      setFormFamilyId(data.course_family_id || undefined);
      
      // Agora o campo mode é sempre um array
      let courseModes: CourseModes = [];
      
      if (Array.isArray(data.mode)) {
        courseModes = data.mode.filter(m => 
          ['onlineAsync', 'onlineSync', 'in-person'].includes(m)
        ) as CourseModes;
      } else if (typeof data.mode === 'string') {
        // Compatibilidade com dados antigos onde mode era string
        courseModes = [data.mode as CourseMode];
      }
      
      // Garantir que há pelo menos um modo válido
      if (courseModes.length === 0) {
        courseModes = ['onlineAsync'];
      }
      
      setFormModes(courseModes);
      
      setFormDurationOnlineAsync(data.duration_onlineasync || undefined);
      setFormDurationOnlineSync(data.duration_onlinesync || undefined);
      setFormDurationInPerson(data.duration_inperson || undefined);
      setFormDurationMinutes(data.duration_minutes || undefined);
      setFormLearningGoals(data.learning_goals || '');
      setFormCoverUrl(data.cover_url || '');
      setFormThumbnailUrl(data.thumbnail_url || '');
      setFormLessonPlanUrl(data.lesson_plan_url || '');
      setFormLessonSlidesUrl(data.lesson_slides_url || '');
      setFormCourseNumber(data.course_number || '');
    } catch (error: any) {
      console.error('Erro ao buscar curso:', error);
      toast.error('Não foi possível carregar os dados do curso');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    if (!formName.trim()) {
      toast.error('O nome do curso é obrigatório');
      return;
    }

    if (formModes.length === 0) {
      toast.error('Selecione pelo menos uma modalidade');
      return;
    }

    setSaving(true);
    try {
      // Verifica se os modos são válidos (apenas 'onlineAsync', 'onlineSync', 'in-person')
      const validModes = ['onlineAsync', 'onlineSync', 'in-person'];
      const validatedModes = formModes.filter(mode => validModes.includes(mode));
      
      if (validatedModes.length === 0) {
        toast.error('Selecione pelo menos uma modalidade válida');
        setSaving(false);
        return;
      }
      
      let finalCourseNumber = formCourseNumber;
      // Generate course number only if creating a new course and it's not set
      if (!isEditMode && !finalCourseNumber) {
          finalCourseNumber = await generateUniqueCode({
            tableName: 'courses',
            columnName: 'course_number',
            generateCodeFunction: generateCourseNumberValue,
            prefix: 'CRS-'
            // No specific fallback needed, the default error/timestamp one is likely fine
          });
      }

      const courseData = {
        name: formName,
        description: formDescription,
        requirements: formRequirements,
        course_family_id: formFamilyId || null,
        mode: validatedModes,
        duration_onlineasync: formDurationOnlineAsync || null,
        duration_onlinesync: formDurationOnlineSync || null,
        duration_inperson: formDurationInPerson || null,
        duration_minutes: formDurationMinutes || null,
        learning_goals: formLearningGoals,
        cover_url: formCoverUrl || null,
        thumbnail_url: formThumbnailUrl || null,
        lesson_plan_url: formLessonPlanUrl || null,
        lesson_slides_url: formLessonSlidesUrl || null,
        course_number: finalCourseNumber,
        is_active: true, // New courses are active by default
        // display_id will be generated after insert/update if needed
      };

      let courseId = id;
      let savedCourse: Course | null = null;

      if (isEditMode) {
        // Update existing course
        const { data, error } = await supabase
          .from('courses')
          .update(courseData)
          .eq('id', id)
          .select()
          .single();

        if (error) throw error;
        savedCourse = data;
        toast.success('Curso atualizado com sucesso!');
      } else {
        // Create new course
        const { data, error } = await supabase
          .from('courses')
          .insert(courseData)
          .select()
          .single();

        if (error) throw error;
        savedCourse = data;
        courseId = savedCourse.id;
        toast.success('Curso criado com sucesso!');
      }
      
      // Generate or ensure display_id after save (if applicable to Courses)
      // Assuming courses also have a display_id like subscriptions
      if (savedCourse && !savedCourse.display_id) { 
          console.log(`Generating display_id for course ${savedCourse.id}`);
          // Example: Generate a simple display ID based on UUID parts
          const generateSimpleDisplayId = (uuid: string) => {
             const cleanUuid = uuid.replace(/-/g, '');
             return `${cleanUuid.substring(0, 6)}-${cleanUuid.substring(cleanUuid.length - 3)}`;
          };
          
          const displayId = await generateUniqueCode({
            tableName: 'courses',
            columnName: 'display_id',
            generateCodeFunction: () => generateSimpleDisplayId(savedCourse!.id),
            fallbackGenerator: generateFallbackDisplayId
          });

          const { error: updateDisplayIdError } = await supabase
            .from('courses')
            .update({ display_id: displayId })
            .eq('id', savedCourse.id);

          if (updateDisplayIdError) {
            console.error('Erro ao atualizar display_id do curso:', updateDisplayIdError);
            toast.warning('Aviso: Falha ao gerar ID de exibição único para o curso.');
          }
      }

      // Upload das imagens se houver arquivos pendentes (novos cursos)
      if (!isEditMode && savedCourse && (pendingCoverFile || pendingThumbnailFile)) {
        try {
          const updates: any = {};
          
          if (pendingCoverFile) {
            const coverResult = await uploadCourseImage({
              courseId: savedCourse.id,
              file: pendingCoverFile,
              type: 'cover'
            });
            updates.cover_url = coverResult.url;
          }
          
          if (pendingThumbnailFile) {
            const thumbnailResult = await uploadCourseImage({
              courseId: savedCourse.id,
              file: pendingThumbnailFile,
              type: 'thumbnail'
            });
            updates.thumbnail_url = thumbnailResult.url;
          }
          
          // Atualizar curso com as URLs das imagens
          if (Object.keys(updates).length > 0) {
            const { error: updateError } = await supabase
              .from('courses')
              .update(updates)
              .eq('id', savedCourse.id);
              
            if (updateError) {
              console.error('Erro ao atualizar URLs das imagens:', updateError);
              toast.warning('Curso criado, mas houve problema no upload das imagens');
            } else {
              toast.success('Curso criado com imagens enviadas com sucesso!');
            }
          }
        } catch (uploadError) {
          console.error('Erro no upload das imagens:', uploadError);
          toast.warning('Curso criado, mas houve problema no upload das imagens');
          }
      }

      navigate(`/courses/${courseId}`);
    } catch (error: any) {
      console.error('Erro ao salvar curso:', error);
      toast.error('Falha ao salvar curso: ' + error.message);
    } finally {
      setSaving(false);
    }
  };



  const goBack = () => {
    navigate('/courses');
  };

  const validateStep = (step: number): boolean => {
    switch (step) {
      case 1:
        return formName.trim() !== '' && formModes.length > 0;
      case 2:
        return true; // Optional fields
      case 3:
        return true; // Optional fields
      default:
        return false;
    }
  };

  const nextStep = () => {
    if (validateStep(currentStep)) {
      if (!completedSteps.includes(currentStep)) {
        setCompletedSteps([...completedSteps, currentStep]);
      }
      if (currentStep < steps.length) {
        setCurrentStep(currentStep + 1);
      }
    } else {
      toast.error('Preencha os campos obrigatórios antes de continuar');
    }
  };

  const prevStep = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
    }
  };

  const goToStep = (step: number) => {
    if (step <= currentStep || completedSteps.includes(step - 1) || step === 1) {
      setCurrentStep(step);
    }
  };

  const getStepStatus = (step: number) => {
    if (completedSteps.includes(step)) return 'completed';
    if (step === currentStep) return 'current';
    if (step < currentStep) return 'completed';
    return 'upcoming';
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <p>Carregando...</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-slate-100">
      {/* Modern Hero Header with Background Image */}
      <div 
        className="relative overflow-hidden" 
        style={{ 
          backgroundColor: '#1F1746',
          backgroundImage: 'url(/elemento-01.svg)',
          backgroundSize: '200%',
          backgroundPosition: 'center',
          backgroundRepeat: 'no-repeat'
        }}
      >
        <div className="absolute inset-0 bg-black/20" />
        <div className="absolute inset-0 bg-gradient-to-br from-white/5 via-transparent to-white/5" />
        
        <div className="relative container mx-auto px-6 py-6 pb-8">
          <div className="flex flex-col lg:flex-row justify-between items-start lg:items-center gap-4">
            <div className="space-y-2">
              <div className="flex items-center gap-3">
                <Button 
                  variant="secondary" 
                  size="default" 
                  onClick={goBack}
                  className="backdrop-blur-sm bg-white/20 border-white/30 text-white hover:bg-white/30 transition-all duration-200"
                >
                  <ArrowLeft className="h-4 w-4 mr-2" />
                  Voltar
                </Button>
                <div className="p-2 bg-white/20 backdrop-blur-sm rounded-lg">
                  <BookOpen className="h-6 w-6 text-white" />
                </div>
                <div>
                  <h1 className="text-2xl font-extrabold text-white">
                    {isEditMode ? 'Editar Curso' : 'Novo Curso'}
                  </h1>
                  <p className="text-blue-100 text-base pb-2">
                    {isEditMode ? 'Atualize as informações do curso' : 'Crie um novo curso no sistema'}
                  </p>
                </div>
              </div>
            </div>

            <div className="flex items-center gap-2">
              {isEditMode && formName && (
                <Badge className="bg-white/20 text-white border-white/30 backdrop-blur-sm px-3 py-1">
                  {formName}
                </Badge>
              )}
            </div>
          </div>
        </div>
      </div>

      <div className="container mx-auto px-6 -mt-4 relative z-10">

        {/* Enhanced Step Navigation */}
        <Card className="border-0 shadow-xl bg-white/80 backdrop-blur-sm mb-8">
          <CardContent className="p-6">
            {/* Mobile Step Navigation */}
            <div className="md:hidden">
              <div className="flex items-center justify-between px-1 mb-4">
                <span className="text-sm font-extrabold text-gray-600">
                  Etapa {currentStep} de {steps.length}
                </span>
                <span className="text-sm text-gray-600">
                  {steps[currentStep - 1]?.title}
                </span>
              </div>
              <div className="flex mt-3 space-x-1">
                {steps.map((step) => {
                  const status = getStepStatus(step.id);
                  return (
                    <button
                      key={step.id}
                      onClick={() => goToStep(step.id)}
                      disabled={step.id > currentStep && !completedSteps.includes(step.id - 1) && step.id !== 1}
                      className={cn(
                        "flex-1 h-3 rounded-full transition-colors",
                        status === 'completed' && "bg-blue-500",
                        status === 'current' && "bg-blue-400",
                        status === 'upcoming' && "bg-gray-200"
                      )}
                    />
                  );
                })}
              </div>
            </div>

            {/* Desktop Step Navigation */}
            <nav className="hidden md:flex items-center justify-center space-x-8">
              {steps.map((step, index) => {
                const status = getStepStatus(step.id);
                const Icon = step.icon;
                
                return (
                  <div key={step.id} className="flex items-center">
                    <button
                      onClick={() => goToStep(step.id)}
                      className={cn(
                        "flex flex-col items-center p-4 rounded-xl transition-all hover:bg-blue-50/50",
                        status === 'current' && "bg-blue-50 border border-blue-200",
                        status === 'completed' && "text-blue-600"
                      )}
                      disabled={step.id > currentStep && !completedSteps.includes(step.id - 1) && step.id !== 1}
                    >
                      <div className={cn(
                        "flex items-center justify-center w-12 h-12 rounded-full border-2 mb-3 transition-colors",
                        status === 'completed' && "bg-blue-500 border-blue-500 text-white",
                        status === 'current' && "border-blue-500 text-blue-500",
                        status === 'upcoming' && "border-gray-300 text-gray-400"
                      )}>
                        {status === 'completed' ? (
                          <CheckCircle className="w-6 h-6" />
                        ) : (
                          <Icon className="w-6 h-6" />
                        )}
                      </div>
                      <div className="text-center">
                        <div className={cn(
                          "text-sm font-extrabold",
                          status === 'completed' && "text-blue-600",
                          status === 'current' && "text-blue-600",
                          status === 'upcoming' && "text-gray-500"
                        )}>
                          {step.title}
                        </div>
                        <div className="text-xs text-gray-500 mt-1">
                          {step.description}
                        </div>
                      </div>
                    </button>
                    {index < steps.length - 1 && (
                      <div className={cn(
                        "w-16 h-px mx-4",
                        completedSteps.includes(step.id) ? "bg-blue-500" : "bg-gray-300"
                      )} />
                    )}
                  </div>
                );
              })}
            </nav>
          </CardContent>
        </Card>

      {/* Main Content */}
      <div className="space-y-8">
        {currentStep === 1 && (
          <Card className="border-0 shadow-xl bg-white/90 backdrop-blur-sm">
            <CardHeader className="pb-6">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-blue-500 to-blue-600 flex items-center justify-center shadow-lg">
                  <FileText className="w-6 h-6 text-white" />
                </div>
                <div>
                  <CardTitle className="text-2xl text-gray-900">Informações Básicas</CardTitle>
                  <CardDescription className="text-gray-600">
                    Configure o nome, família e modalidades do curso
                  </CardDescription>
                </div>
              </div>
            </CardHeader>
              <CardContent className="space-y-8">
                {/* Course Code Section */}
                {isEditMode && formCourseNumber && (
                  <div className="p-4 bg-muted/30 rounded-lg border border-dashed">
                    <div className="flex items-center justify-between">
                      <div>
                        <Label className="text-sm font-extrabold text-muted-foreground">
                      Código do Curso
                    </Label>
                        <p className="text-lg font-mono font-extrabold mt-1">
                          {formCourseNumber}
                        </p>
                      </div>
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={() => {
                          navigator.clipboard.writeText(formCourseNumber);
                          toast.success('Código copiado!');
                        }}
                        className="gap-2"
                      >
                        <Copy className="h-4 w-4" />
                        Copiar
                      </Button>
                    </div>
                  </div>
                )}

                {/* Basic Information */}
                <div className="grid gap-8 lg:grid-cols-2">
                  <div className="space-y-4">
                    <div>
                      <Label htmlFor="name" className="text-base font-extrabold">
                        Nome do Curso*
                  </Label>
                      <p className="text-sm text-muted-foreground mb-2">
                        Escolha um nome claro e descritivo para o curso
                      </p>
                  <Input
                    id="name"
                    value={formName}
                    onChange={(e) => setFormName(e.target.value)}
                        className={cn(
                          "text-base h-11",
                          !formName.trim() && "border-destructive/50"
                        )}
                        placeholder="Ex: Introdução ao Marketing Digital"
                      />
                      {!formName.trim() && (
                        <p className="text-sm text-destructive mt-1">
                          O nome do curso é obrigatório
                        </p>
                      )}
                </div>
                  </div>

                  <div className="space-y-4">
                    <div>
                      <Label htmlFor="family" className="text-base font-extrabold">
                    Família de Cursos
                  </Label>
                      <p className="text-sm text-muted-foreground mb-2">
                        Agrupe o curso em uma categoria para melhor organização
                      </p>
                  <Select
                    value={formFamilyId}
                    onValueChange={(value) => setFormFamilyId(value)}
                  >
                        <SelectTrigger className="text-base h-11">
                          <SelectValue placeholder="Selecione uma família de cursos" />
                    </SelectTrigger>
                    <SelectContent>
                      {courseFamilies.map((family) => (
                        <SelectItem key={family.id} value={family.id}>
                          {family.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                  </div>
                </div>

                {/* Course Modes Section */}
                <div>
                  <Label className="text-base font-extrabold">
                    Modalidades de Ensino*
                  </Label>
                  <p className="text-sm text-muted-foreground mb-4">
                    Selecione as modalidades disponíveis para este curso
                  </p>
                  
                  <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-3 gap-6">
                    <div className={cn(
                      "relative p-4 border rounded-lg cursor-pointer transition-colors hover:border-primary/50",
                      formModes.includes('onlineAsync') 
                        ? "border-primary bg-primary/5" 
                        : "border-muted-foreground/30"
                    )}
                    onClick={() => {
                      if (formModes.includes('onlineAsync')) {
                        setFormModes(formModes.filter(mode => mode !== 'onlineAsync'));
                        setFormDurationOnlineAsync(undefined); // Clear duration when mode is unchecked
                      } else {
                        setFormModes([...formModes, 'onlineAsync']);
                      }
                    }}>
                      <Checkbox 
                        id="mode-onlineAsync" 
                        checked={formModes.includes('onlineAsync')}
                        className="absolute top-3 right-3"
                      />
                      <div className="pr-8">
                        <h4 className="font-extrabold">Online Assíncrono</h4>
                        <p className="text-sm text-muted-foreground mt-1">
                          Conteúdo gravado, acesso flexível
                        </p>
                    </div>
                    </div>

                    <div className={cn(
                      "relative p-4 border rounded-lg cursor-pointer transition-colors hover:border-primary/50",
                      formModes.includes('onlineSync') 
                        ? "border-primary bg-primary/5" 
                        : "border-muted-foreground/30"
                    )}
                    onClick={() => {
                      if (formModes.includes('onlineSync')) {
                        setFormModes(formModes.filter(mode => mode !== 'onlineSync'));
                        setFormDurationOnlineSync(undefined); // Clear duration when mode is unchecked
                      } else {
                        setFormModes([...formModes, 'onlineSync']);
                      }
                    }}>
                      <Checkbox 
                        id="mode-onlineSync" 
                        checked={formModes.includes('onlineSync')}
                        className="absolute top-3 right-3"
                      />
                      <div className="pr-8">
                        <h4 className="font-extrabold">Online Síncrono</h4>
                        <p className="text-sm text-muted-foreground mt-1">
                          Aulas ao vivo, horário fixo
                        </p>
                    </div>
                    </div>

                    <div className={cn(
                      "relative p-4 border rounded-lg cursor-pointer transition-colors hover:border-primary/50",
                      formModes.includes('in-person') 
                        ? "border-primary bg-primary/5" 
                        : "border-muted-foreground/30"
                    )}
                    onClick={() => {
                      if (formModes.includes('in-person')) {
                        setFormModes(formModes.filter(mode => mode !== 'in-person'));
                        setFormDurationInPerson(undefined); // Clear duration when mode is unchecked
                      } else {
                        setFormModes([...formModes, 'in-person']);
                      }
                    }}>
                      <Checkbox 
                        id="mode-inperson" 
                        checked={formModes.includes('in-person')}
                        className="absolute top-3 right-3"
                      />
                      <div className="pr-8">
                        <h4 className="font-extrabold">Presencial</h4>
                        <p className="text-sm text-muted-foreground mt-1">
                          Aulas físicas, local definido
                        </p>
                    </div>
                  </div>
                </div>
                
                  {formModes.length === 0 && (
                    <p className="text-sm text-destructive mt-2">
                      Selecione pelo menos uma modalidade
                    </p>
                  )}
                </div>
                
                {/* Duration Section */}
                {formModes.length > 0 && (
                  <div>
                    <Label className="text-base font-extrabold">
                      Duração do Curso
                    </Label>
                    <p className="text-sm text-muted-foreground mb-4">
                      Configure a duração em minutos para cada modalidade selecionada
                    </p>
                    
                    <div className="grid gap-4 lg:grid-cols-3">
                {formModes.includes('onlineAsync') && (
                        <div className="p-4 border rounded-lg bg-muted/20">
                          <div className="flex items-center justify-between mb-2">
                            <h4 className="font-extrabold">Online Assíncrono</h4>
                            <Badge variant="secondary">Gravado</Badge>
                          </div>
                          <div className="flex items-center gap-3">
                    <Input
                      id="durationOnlineAsync"
                      type="number"
                      value={formDurationOnlineAsync || ''}
                      onChange={(e) => setFormDurationOnlineAsync(e.target.value ? parseInt(e.target.value) : undefined)}
                              className="text-base h-10 max-w-32"
                              placeholder="60"
                              min="0"
                    />
                            <span className="text-sm text-muted-foreground">minutos</span>
                          </div>
                  </div>
                )}
                    
                {formModes.includes('onlineSync') && (
                        <div className="p-4 border rounded-lg bg-muted/20">
                          <div className="flex items-center justify-between mb-2">
                            <h4 className="font-extrabold">Online Síncrono</h4>
                            <Badge variant="secondary">Ao vivo</Badge>
                          </div>
                          <div className="flex items-center gap-3">
                    <Input
                      id="durationOnlineSync"
                      type="number"
                      value={formDurationOnlineSync || ''}
                      onChange={(e) => setFormDurationOnlineSync(e.target.value ? parseInt(e.target.value) : undefined)}
                              className="text-base h-10 max-w-32"
                              placeholder="90"
                              min="0"
                    />
                            <span className="text-sm text-muted-foreground">minutos</span>
                          </div>
                  </div>
                )}
                    
                {formModes.includes('in-person') && (
                        <div className="p-4 border rounded-lg bg-muted/20">
                          <div className="flex items-center justify-between mb-2">
                            <h4 className="font-extrabold">Presencial</h4>
                            <Badge variant="secondary">Físico</Badge>
                          </div>
                          <div className="flex items-center gap-3">
                    <Input
                      id="durationInPerson"
                      type="number"
                      value={formDurationInPerson || ''}
                      onChange={(e) => setFormDurationInPerson(e.target.value ? parseInt(e.target.value) : undefined)}
                              className="text-base h-10 max-w-32"
                              placeholder="120"
                              min="0"
                    />
                            <span className="text-sm text-muted-foreground">minutos</span>
                          </div>
                      </div>
                    )}
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          )}
        
        {currentStep === 2 && (
          <Card className="border-0 shadow-xl bg-white/90 backdrop-blur-sm">
            <CardHeader className="pb-6">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-purple-500 to-purple-600 flex items-center justify-center shadow-lg">
                  <Settings className="w-6 h-6 text-white" />
                </div>
                <div>
                  <CardTitle className="text-2xl text-gray-900">Conteúdo do Curso</CardTitle>
                  <CardDescription className="text-gray-600">
                    Defina descrição, objetivos e requisitos do curso
                  </CardDescription>
                </div>
              </div>
            </CardHeader>
              <CardContent className="space-y-8">
                <div className="space-y-8">
                  <div>
                    <Label htmlFor="description" className="text-base font-extrabold">
                      Descrição do Curso
                  </Label>
                    <p className="text-sm text-muted-foreground mb-3">
                      Forneça uma descrição clara e atraente do curso
                    </p>
                  <Textarea
                    id="description"
                    value={formDescription}
                    onChange={(e) => setFormDescription(e.target.value)}
                      className="text-base min-h-[120px] resize-none"
                      placeholder="Ex: Este curso oferece uma introdução completa ao marketing digital, cobrindo estratégias de SEO, redes sociais e análise de dados..."
                    />
                    <div className="flex justify-between items-center mt-2">
                      <span className="text-xs text-muted-foreground">
                        Uma boa descrição ajuda os alunos a entender o valor do curso
                      </span>
                      <span className="text-xs text-muted-foreground">
                        {formDescription.length} caracteres
                      </span>
                    </div>
                </div>
                
                  <Separator />
                  
                  <div>
                    <Label htmlFor="learningGoals" className="text-base font-extrabold">
                    Objetivos de Aprendizagem
                  </Label>
                    <p className="text-sm text-muted-foreground mb-3">
                      Liste o que os alunos serão capazes de fazer após completar o curso
                    </p>
                  <Textarea
                    id="learningGoals"
                    value={formLearningGoals}
                    onChange={(e) => setFormLearningGoals(e.target.value)}
                      className="text-base min-h-[120px] resize-none"
                      placeholder="Ex: • Criar campanhas de marketing digital eficazes&#10;• Analisar métricas de desempenho&#10;• Implementar estratégias de SEO&#10;• Gerenciar redes sociais profissionalmente"
                    />
                    <div className="flex justify-between items-center mt-2">
                      <span className="text-xs text-muted-foreground">
                        Use listas para facilitar a leitura
                      </span>
                      <span className="text-xs text-muted-foreground">
                        {formLearningGoals.length} caracteres
                      </span>
                    </div>
                </div>
                
                  <Separator />
                  
                  <div>
                    <Label htmlFor="requirements" className="text-base font-extrabold">
                      Pré-requisitos
                  </Label>
                    <p className="text-sm text-muted-foreground mb-3">
                      Conhecimentos prévios ou materiais necessários para o curso
                    </p>
                  <Textarea
                    id="requirements"
                    value={formRequirements}
                    onChange={(e) => setFormRequirements(e.target.value)}
                      className="text-base min-h-[120px] resize-none"
                      placeholder="Ex: • Conhecimento básico de internet&#10;• Acesso a computador ou smartphone&#10;• Motivação para aprender"
                    />
                    <div className="flex justify-between items-center mt-2">
                      <span className="text-xs text-muted-foreground">
                        Liste requisitos claros para evitar frustrações
                      </span>
                      <span className="text-xs text-muted-foreground">
                        {formRequirements.length} caracteres
                      </span>
                    </div>
                  </div>
              </div>
            </CardContent>
          </Card>
          )}
        
        {currentStep === 3 && (
          <Card className="border-0 shadow-xl bg-white/90 backdrop-blur-sm">
            <CardHeader className="pb-6">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-green-500 to-green-600 flex items-center justify-center shadow-lg">
                  <Image className="w-6 h-6 text-white" />
                </div>
                <div>
                  <CardTitle className="text-2xl text-gray-900">Recursos e Mídia</CardTitle>
                  <CardDescription className="text-gray-600">
                    Adicione imagens, planos de aula e materiais didáticos
                  </CardDescription>
                </div>
              </div>
            </CardHeader>
              <CardContent className="space-y-8">
                {/* Visual Assets Section */}
                <div>
                  <h3 className="text-lg font-extrabold mb-4">Recursos Visuais</h3>
                  <div className="grid gap-8 md:grid-cols-2 lg:grid-cols-2">
                    <div className="space-y-3">
                <ImageUpload
                  label="Imagem de Capa"
                  value={formCoverUrl}
                  onChange={setFormCoverUrl}
                        onUpload={async (file) => {
                          if (isEditMode && id) {
                            // Modo edição: upload direto
                            const result = await uploadCourseImage({
                      courseId: id,
                      file,
                      type: 'cover'
                    });
                            return result;
                          } else {
                            // Novo curso: armazenar arquivo para upload posterior
                            setPendingCoverFile(file);
                            // Gerar preview local
                            const { generateImagePreview } = await import('@/services/uploadService');
                            const previewUrl = await generateImagePreview(file);
                            return { url: previewUrl };
                          }
                        }}
                  placeholder="https://exemplo.com/imagem-capa.jpg"
                  description="Recomendado: 1200x630px (formato paisagem)"
                  previewSize="large"
                        uploadOnly={!isEditMode}
                />
                      <p className="text-xs text-muted-foreground">
                        Será exibida nas listagens e página principal do curso
                      </p>
                    </div>

                    <div className="space-y-3">
                <ImageUpload
                  label="Miniatura"
                  value={formThumbnailUrl}
                  onChange={setFormThumbnailUrl}
                        onUpload={async (file) => {
                          if (isEditMode && id) {
                            // Modo edição: upload direto
                            const result = await uploadCourseImage({
                      courseId: id,
                      file,
                      type: 'thumbnail'
                    });
                            return result;
                          } else {
                            // Novo curso: armazenar arquivo para upload posterior
                            setPendingThumbnailFile(file);
                            // Gerar preview local
                            const { generateImagePreview } = await import('@/services/uploadService');
                            const previewUrl = await generateImagePreview(file);
                            return { url: previewUrl };
                          }
                        }}
                  placeholder="https://exemplo.com/miniatura.jpg"
                  description="Recomendado: 400x400px (formato quadrado)"
                  previewSize="small"
                        uploadOnly={!isEditMode}
                      />
                      <p className="text-xs text-muted-foreground">
                        Usada em cards e listagens compactas
                      </p>
                    </div>
                  </div>
                </div>

                <Separator />

                {/* Learning Materials Section */}
                <div>
                  <h3 className="text-lg font-extrabold mb-4">Materiais Didáticos</h3>
                  <div className="grid gap-6">
                    <div className="p-4 border rounded-lg bg-gradient-to-r from-blue-50/50 to-indigo-50/50 dark:from-blue-950/20 dark:to-indigo-950/20">
                      <div className="flex items-start gap-3">
                        <div className="w-10 h-10 rounded-lg bg-blue-100 dark:bg-blue-900/50 flex items-center justify-center mt-1">
                          <FileText className="w-5 h-5 text-blue-600 dark:text-blue-400" />
                        </div>
                        <div className="flex-1 space-y-3">
                          <div>
                            <Label htmlFor="lessonPlanUrl" className="text-base font-extrabold">
                              Plano de Aula
                  </Label>
                            <p className="text-sm text-muted-foreground">
                              Documento com estrutura detalhada da aula
                            </p>
                          </div>
                  <Input
                    id="lessonPlanUrl"
                    value={formLessonPlanUrl}
                    onChange={(e) => setFormLessonPlanUrl(e.target.value)}
                            className="text-base h-11"
                            placeholder="https://triviumeducacao-my.sharepoint.com/..."
                  />
                        </div>
                      </div>
                </div>

                    <div className="p-4 border rounded-lg bg-gradient-to-r from-green-50/50 to-emerald-50/50 dark:from-green-950/20 dark:to-emerald-950/20">
                      <div className="flex items-start gap-3">
                        <div className="w-10 h-10 rounded-lg bg-green-100 dark:bg-green-900/50 flex items-center justify-center mt-1">
                          <Image className="w-5 h-5 text-green-600 dark:text-green-400" />
                        </div>
                        <div className="flex-1 space-y-3">
                          <div>
                            <Label htmlFor="lessonSlidesUrl" className="text-base font-extrabold">
                              Slides da Apresentação
                  </Label>
                            <p className="text-sm text-muted-foreground">
                              Apresentação visual para apoio às aulas
                            </p>
                          </div>
                  <Input
                    id="lessonSlidesUrl"
                    value={formLessonSlidesUrl}
                    onChange={(e) => setFormLessonSlidesUrl(e.target.value)}
                            className="text-base h-11"
                            placeholder="https://triviumeducacao-my.sharepoint.com/..."
                          />
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="mt-4 p-3 bg-amber-50/50 dark:bg-amber-950/20 border border-amber-200/50 dark:border-amber-800/50 rounded-lg">
                    <p className="text-sm text-amber-800 dark:text-amber-200">
                      <strong>Dica:</strong> Todos os recursos são opcionais. Você pode adicionar ou atualizar esses links a qualquer momento após criar o curso.
                    </p>
                  </div>
              </div>
            </CardContent>
          </Card>
          )}
      </div>

        {/* Enhanced Navigation Footer */}
        <Card className="border-0 shadow-xl bg-white/90 backdrop-blur-sm mt-8">
          <CardContent className="p-6">
            <div className="flex flex-col sm:flex-row items-stretch sm:items-center justify-between gap-4">
              <Button
                variant="outline"
                onClick={prevStep}
                disabled={currentStep === 1}
                className="flex items-center justify-center gap-2 order-2 sm:order-1 bg-white border-gray-200 hover:bg-gray-50"
                size="lg"
              >
                <ChevronLeft className="w-4 h-4" />
                Anterior
              </Button>
              
              <div className="flex items-center justify-center gap-2 order-1 sm:order-2">
                {steps.map((step) => {
                  const status = getStepStatus(step.id);
                  return (
                    <div
                      key={step.id}
                      className={cn(
                        "w-3 h-3 rounded-full transition-colors",
                        status === 'completed' && "bg-blue-500",
                        status === 'current' && "bg-blue-400",
                        status === 'upcoming' && "bg-gray-300"
                      )}
                    />
                  );
                })}
              </div>
              
              <div className="flex items-center gap-3 order-3">
                {currentStep < steps.length ? (
                  <Button
                    onClick={nextStep}
                    className="flex items-center justify-center gap-2 w-full sm:w-auto bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700"
                    disabled={!validateStep(currentStep)}
                    size="lg"
                  >
                    Próximo
                    <ChevronRight className="w-4 h-4" />
                  </Button>
                ) : (
                  <Button
                    onClick={handleSave}
                    disabled={saving}
                    className="flex items-center justify-center gap-2 px-6 w-full sm:w-auto bg-gradient-to-r from-green-600 to-green-700 hover:from-green-700 hover:to-green-800"
                    size="lg"
                  >
                    <Save className="w-4 h-4" />
                    {saving ? 'Salvando...' : 'Salvar Curso'}
                  </Button>
                )}
              </div>
            </div>
          </CardContent>
        </Card>
        
        {/* Bottom Spacing */}
        <div className="h-16" />
      </div>
    </div>
  );
};

export default CourseForm; 